import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { removeStorageFile } from '@/lib/storage';
import { canWrite, canDelete, WRITE_PERMISSION_DENIED, DELETE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';

export interface Expense {
  id: string;
  category: string;
  description: string;
  amount: number;
  expenseDate: string;
  receiptUrl: string;
  createdAt: string;
}

export interface ExpensesResult {
  data: Expense[];
}

export const EXPENSE_CATEGORIES: Record<string, string> = {
  transport: 'Transporte',
  packaging: 'Empaques',
  operation: 'Operación',
  supplies: 'Insumos',
  rent: 'Arriendo',
  payroll: 'Nómina',
  taxes: 'Impuestos',
  services: 'Servicios',
  other: 'Otro',
};

export async function fetchExpenses(): Promise<ExpensesResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const { data, error } = await supabase.from('expenses').select('*');

    if (error) {
      throw new Error(error.message);
    }

    const mapped: Expense[] = ((data ?? []) as Array<Record<string, any>>)
      .map((r) => ({
        id: r.id,
        category: r.category,
        description: r.description,
        amount: Number(r.amount),
        expenseDate: r.expense_date ?? '',
        receiptUrl: r.receipt_url ?? '',
        createdAt: r.created_at ?? '',
      }))
      .sort((a, b) => (a.expenseDate < b.expenseDate ? 1 : -1));

    return { data: mapped };
  } catch (err) {
    console.error('[expenses] connection failed:', err);
    throw err;
  }
}

export interface ExpenseInput {
  category: string;
  description: string;
  amount: number;
  expenseDate: string;
  receiptUrl: string;
}

export async function saveExpense(
  input: ExpenseInput,
): Promise<{ data: Expense | null; error: string | null }> {
  if (!(await canWrite('expenses'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const payload = {
    category: input.category,
    description: input.description.trim(),
    amount: Number(input.amount),
    expense_date: input.expenseDate || new Date().toISOString(),
    receipt_url: input.receiptUrl.trim() || null,
  };

  try {
    const { data, error } = await supabase.from('expenses').insert(payload).select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as Record<string, any> | undefined;
    if (!row) return { data: null, error: 'No se pudo leer el gasto guardado.' };
    recordAudit({
      entityType: 'expenses',
      entityId: row.id,
      action: 'CREATE',
      newValue: `${input.description} - $${input.amount}`,
    });
    return {
      data: {
        id: row.id,
        category: row.category,
        description: row.description,
        amount: Number(row.amount),
        expenseDate: row.expense_date ?? '',
        receiptUrl: row.receipt_url ?? '',
        createdAt: row.created_at ?? '',
      },
      error: null,
    };
  } catch (err) {
    console.warn('[expenses] save failed:', err);
    return { data: null, error: 'Error de conexión al guardar el gasto.' };
  }
}

// Actualiza un gasto existente (categoría, descripción, monto, fecha y soporte).
export async function updateExpense(
  id: string,
  input: ExpenseInput,
): Promise<{ error: string | null }> {
  if (!(await canWrite('expenses'))) {
    return { error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { error: 'Supabase no está configurado.' };
  }

  const payload = {
    category: input.category,
    description: input.description.trim(),
    amount: Number(input.amount),
    expense_date: input.expenseDate || new Date().toISOString(),
    receipt_url: input.receiptUrl.trim() || null,
  };

  try {
    const { error } = await supabase.from('expenses').update(payload).eq('id', id);
    if (!error) {
      recordAudit({
        entityType: 'expenses',
        entityId: id,
        action: 'UPDATE',
        fieldName: 'description',
        newValue: `${input.description} - $${input.amount}`,
      });
    }
    return { error: error?.message ?? null };
  } catch (err) {
    console.warn('[expenses] update failed:', err);
    return { error: 'Error de conexión al actualizar el gasto.' };
  }
}

// Elimina un gasto de forma permanente.
export async function deleteExpense(id: string): Promise<{ error: string | null }> {
  if (!(await canDelete('expenses'))) {
    return { error: DELETE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { error: 'Supabase no está configurado.' };
  }

  try {
    const { error } = await supabase.from('expenses').delete().eq('id', id);
    if (error) return { error: error.message };
    recordAudit({ entityType: 'expenses', entityId: id, action: 'DELETE' });
    return { error: null };
  } catch (err) {
    console.warn('[expenses] delete failed:', err);
    return { error: 'Error de conexión al eliminar el gasto.' };
  }
}

// Sube el comprobante/soporte de un gasto al bucket público `receipts` y devuelve
// la URL pública lista para guardar en `receipt_url`. El nombre se sanitiza
// (minúsculas, sin espacios ni caracteres raros) y se antepone un timestamp
// para evitar colisiones.
export async function uploadExpenseReceipt(
  file: File,
): Promise<{ url: string | null; error: string | null }> {
  if (!isSupabaseConfigured || !supabase) {
    return { url: null, error: 'Supabase no está configurado. Conéctalo para subir archivos.' };
  }

  const safeBase =
    file.name
      .replace(/\.[^.]+$/, '')
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'comprobante';

  const ext = file.name.split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'pdf';
  const path = `${Date.now()}-${safeBase}.${ext}`;

  try {
    const { error } = await supabase.storage
      .from('receipts')
      .upload(path, file, { cacheControl: '3600', upsert: false });

    if (error) return { url: null, error: error.message };

    const { data } = supabase.storage.from('receipts').getPublicUrl(path);
    return { url: data.publicUrl, error: null };
  } catch (err) {
    console.warn('[expenses] upload receipt failed:', err);
    return { url: null, error: 'Error de conexión al subir el comprobante.' };
  }
}

// Borra el comprobante de un gasto del bucket `receipts` a partir de su URL
// pública. Si la URL es externa (no de Storage propio), no hace nada.
export async function deleteExpenseReceipt(url: string): Promise<{ error: string | null }> {
  return removeStorageFile('receipts', url);
}